import { useEffect, useRef } from 'react';
import { ContentfulAsset } from '../features/Contentful';
import { useS4XMastheadContext } from '@spotify-internal/s4x-masthead';
import {
  SecondaryNavProps,
  LinkType,
  PrimaryLinkType,
} from '@spotify-internal/frodor-modules';
import { URL } from 'url';

type ContentfulPrimaryLinkType = {
  href: string;
  label: string;
  openInNewTab: boolean;
  trackingLabel: string;
  image: ContentfulAsset;
  id: string;
  contentType: 'mastheadSecondaryNavPrimaryLink';
};

type ContentfulLinkType = {
  label: string;
  href: string;
  openInNewTab: boolean;
  trackingLabel: string;
  id: string;
  contentType: 'mastheadTextLink';
};

export interface ContentfulSecondaryNavigationType {
  primaryLink: ContentfulPrimaryLinkType;
  links: ContentfulLinkType[];
  id: string;
  contentType: 'mastheadSecondaryNav';
}

const normalizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url, 'http://placeholder'); // Use a placeholder base for relative URLs
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    return pathSegments[pathSegments.length - 1] || ''; // Extracts the last segment as the URL slug
  } catch {
    // If URL constructor fails, assume it's a relative URL
    const pathSegments = url.split('/').filter(Boolean);
    return pathSegments[pathSegments.length - 1] || ''; // Extracts the last segment as the URL slug
  }
};

const formatLink = (
  link: ContentfulLinkType | ContentfulPrimaryLinkType,
  currentUrl: string,
): PrimaryLinkType | LinkType => {
  if (link.contentType === 'mastheadTextLink') {
    return {
      href: link.href,
      label: link.label,
      openInNewTab: link.openInNewTab,
      trackingLabel: link.trackingLabel,
      isActive: normalizeUrl(link.href) === currentUrl,
    };
  }
  const image = (link as ContentfulPrimaryLinkType).image;
  return {
    href: link.href,
    label: link.label,
    openInNewTab: link.openInNewTab,
    trackingLabel: link.trackingLabel,
    trackCat: 'Top Nav',
    imageUrl: image?.file.url || '',
    isActive: normalizeUrl(link.href) === currentUrl,
  };
};

/**
 * A custom hook that sets up the secondary navigation for the masthead
 * based on the provided template.
 *
 * @param {any} template - The template object containing the secondary navigation
 * configuration and URL slug. Works with any template type that has
 * secondaryNavigation and urlSlug properties.
 * @param {boolean} [forceCleanup] - Optional flag to force cleanup of secondary navigation
 *
 * @remarks
 * This hook uses the `useEffect` hook to update the secondary navigation
 * in the masthead context whenever the `secondaryNavigation` or `urlSlug`
 * properties of the template change. It formats the primary link and
 * additional links using the `formatLink` utility before passing them
 * to the masthead context. The hook automatically cleans up the secondary
 * navigation when the component unmounts or when forceCleanup is true.
 * Uses a ref to prevent infinite re-renders by only updating when the
 * secondaryNavigation actually changes.
 *
 * @example
 * ```tsx
 * const template = {
 *   secondaryNavigation: {
 *     primaryLink: { label: 'Home', href: '/' },
 *     links: [{ label: 'About', href: '/about' }],
 *   },
 *   urlSlug: 'example-slug',
 * };
 * useSecondaryNav(template);
 *
 * // Force cleanup
 * useSecondaryNav(template, true);
 * ```
 */
export const useSecondaryNav = (template: any, forceCleanup?: boolean) => {
  const {
    secondaryNavigation: mastheadSecondaryNavigation,
    setSecondaryNavigation,
  } = useS4XMastheadContext();
  const { secondaryNavigation, urlSlug } = template;
  const previousSecondaryNavigation =
    useRef<typeof secondaryNavigation>(undefined);

  useEffect(() => {
    // Only update if secondaryNavigation has actually changed
    if (secondaryNavigation !== previousSecondaryNavigation.current) {
      previousSecondaryNavigation.current = secondaryNavigation;

      if (secondaryNavigation !== undefined) {
        const formattedPrimaryLink = formatLink(
          secondaryNavigation.primaryLink,
          urlSlug ?? '',
        ) as PrimaryLinkType;
        const formattedLinks = secondaryNavigation.links
          .filter((link: any) => link.href && link.label) // Filter out links with empty href or label
          .map((link: any) => formatLink(link, urlSlug ?? ''));
        const formattedSecondaryNavigation: SecondaryNavProps = {
          primaryLink: formattedPrimaryLink,
          links: formattedLinks,
          trackCat: 'Top Nav',
        };
        setSecondaryNavigation(formattedSecondaryNavigation);
      } else if (mastheadSecondaryNavigation) {
        // Remove the secondary navigation if one is set and it doesn't exist in the new template
        setSecondaryNavigation(undefined);
      }
    }
  }, [
    secondaryNavigation,
    setSecondaryNavigation,
    urlSlug,
    mastheadSecondaryNavigation,
  ]);

  // Cleanup effect: clear secondary navigation when component unmounts or forceCleanup is true
  useEffect(() => {
    if (forceCleanup) {
      setSecondaryNavigation(undefined);
    }

    return () => {
      // This cleanup function runs when the component unmounts
      setSecondaryNavigation(undefined);
    };
  }, [setSecondaryNavigation, forceCleanup]);
};
